import {ENDPOINT} from 'constants/endpoints';
import {ERROR_FIELD} from 'constants/error-fields';
import {Formik} from 'formik'
import cloneDeep from 'lodash/cloneDeep'
import isArray from 'lodash/isArray'
import merge from 'lodash/merge'
import React, {Fragment, useEffect, useState} from 'react'
import {toast} from 'react-toastify'
import BasicLabel from '../../../../../components/BasicLabel'
import BasicSlideToggle from '../../../../../components/BasicSlideToggle'
import FormContentLoader from '../../../../../components/FormContentLoader'
import TooltipIcon from '../../../../../components/TooltipIcon'
import {Role} from '../../../../../helpers/user'
import useApi from '../../../../../hooks/useApi'
import ResponseErrorDialog from '../../../../Dialog/components/ResponseErrorDialog'
import RequestDeleteAccount from '../../../../RequestDeleteAccount'
import {AccountSettingsFormValidationSchema} from '../validate'

const PrivacySection = props => {
  const {sections, user} = props

  const initData = {
    isFetching: true,
    fields: {
      privacy: [],
      blackList: [],
      ChatState: 'Offline',
    },
    options: {
      privacy: [],
      blackList: [],
    },
    permissions: {
      AllowedChantSettingsGoOffline: false,
      AllowedChangeEmail: false,
      AllowedChantSettingsEdit: false,
      AllowedPersonalCalendar: false,
      AllowedRecruitersProcessDigest: false,
    },
  };

  const api = useApi();
  const [formData, setFormData] = useState(initData);
  const [lockDeleteAccount, setLockDeleteAccount] = useState(false);
  const [delayTimer, setDelayTimer] = useState(null);

  useEffect(() => {
    api.get(
      ENDPOINT.FORM.GET.ACCOUNT_SETTINGS,
      {},
      {
        handleError: response => ResponseErrorDialog({ response }),
        handleSuccess: formData => {
          return setFormData({
            ...merge(initData, formData),
            isFetching: false,
          })
        },
      }
    );
  }, []);

  const scrollToError = () => {
    if (document.getElementsByClassName('form__error').length > 0) {
      const elem = document.getElementsByClassName('form__error')[0];
      if (elem.previousElementSibling) {
        elem.previousElementSibling.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'start',
        });
      }
    }
  };

  // let delayTimer = null;

  const onSubmit = (values, { resetForm, setSubmitting, setErrors }) => {
    setLockDeleteAccount(true);
    if (delayTimer) {
      clearTimeout(delayTimer)
      setDelayTimer(null);
    }
    setDelayTimer(setTimeout(() => {
      setLockDeleteAccount(true);
      setSubmitting(true)
      const preparedValues = cloneDeep(values);
      Object.keys(preparedValues).forEach(field => {
        let data = preparedValues[field];
        if (
          preparedValues[field] === null ||
          field === 'changeEmailRequestTo' ||
          field === 'EmailAddress'
        ) {
          delete preparedValues[field];
        } else if (isArray(data) && data.length === 0) {
          delete preparedValues[field];
        }
      });
      const apiCall = new Promise((resolve, reject) => {
        api.post(ENDPOINT.FORM.SAVE.ACCOUNT_SETTINGS, preparedValues, {
          handleError: response => {
            onSubmitError(response, setErrors)
            reject(response)
          },
          handleSuccess: () => {
            // BasicDialog({ bodyRender: <p>Your account data has been updated.</p> });
            // resetForm({ ...values });
            resolve()
          },
          handleComplete: () => {
            setDelayTimer(null);
            setLockDeleteAccount(false);
            setSubmitting(false)
          },
        });
      });
      toast.promise(apiCall, {
        pending: {
          render(){
            return <p>Saving...</p>
          },
          icon: false,
        },
        success: {
          render(){
            return <p>Your account data has been updated.</p>
          },
          // other options
          icon: "🟢",
        },
        error: {
          render({data}){
            const {
              data: { fields },
            } = data;
            if (fields && typeof setErrors === 'function') {
              const obj = {};
              for (let [key, value] of Object.entries(fields)) {
                const error = isArray(value) ? value[0] : value;
                obj[key] = ERROR_FIELD[error] || `${error}`;
              }
              return Object.values(obj).join('<br />')
            } else {
              // When the promise reject, data will contains the error
              return `${data.status}`
            }
          }
        }
      });
    }, 500))


  };

  const onSubmitError = (response, setErrors) => {
    const {
      data: { fields },
    } = response;
    if (fields && typeof setErrors === 'function') {
      const obj = {};
      for (let [key, value] of Object.entries(fields)) {
        const error = isArray(value) ? value[0] : value;
        obj[key] = ERROR_FIELD[error] || `${error}`;
      }
      setErrors(obj);
      scrollToError();
    } else {
      ResponseErrorDialog({ response });
    }
  };

  const handleChangeCheckboxArray = (
    field,
    value,
    { values, setFieldValue }
  ) => {
    const val = values[field].includes(value)
      ? values[field].filter(v => v !== value)
      : values[field].concat(value)
    setFieldValue(
      field,
      val
    );
    return val;
  };

  const handleChangeField = (field, checked, {value, values, emailField, setFieldValue, handleSubmit, resetForm, setSubmitting, setErrors}) => {
    switch (field){
      case 'blackList':
      case 'privacy':
      case 'processApplic':
      case 'processApplicDigest':
        const val = handleChangeCheckboxArray(field, value, {
          values,
          setFieldValue,
        })
        values[field] = val;
        onSubmit(values, {resetForm, setSubmitting, setErrors});
        break;
      default:
        setFieldValue(field, value);
        values[field] = value;
        onSubmit(values, {resetForm, setSubmitting, setErrors});
        break;
    }

  }

  const allowedPrivacy = Role.isCandidate(user) && formData.options.privacy;
  const allowedChantSettingsEdit =
    formData.permissions.AllowedChantSettingsEdit;
  const allowedChantGoOffline =
    formData.permissions.AllowedChantSettingsGoOffline;

  return (
    <Formik
      enableReinitialize
      initialValues={formData.fields}
      validationSchema={AccountSettingsFormValidationSchema}
      onSubmit={onSubmit}
    >
      {({
          isValidating,
          values,
          handleChange,
          handleSubmit,
          isSubmitting,
          errors,
          touched,
          handleBlur,
          setFieldValue,
          setFieldTouched,
          submitForm,
          setSubmitting,
          resetForm,
          setErrors
        }) => (
        <form
          onSubmit={e => {
            e.preventDefault();
            submitForm().then(() =>
              isValidating ? scrollToError() : null
            );
          }}
          className="form__content"
          autoComplete="off"
        >
          <div className="form__header"><a name="privacy"></a>
            <h4 className="form__title" >{sections['privacy'].caption}</h4>
          </div>
          {formData.isFetching && <FormContentLoader />}
          {!formData.isFetching && !allowedPrivacy && (
            <div className="form__group" style={{ marginBottom: '15px' }}>
              <BasicLabel
                name="privacyOptions"
                required={false}
                text="Privacy Options"
                styles={{
                  marginBottom: '5px',
                  fontWeight: '700',
                }}
              />
              <p>Options are unavailable until Candidate Profile is active. Please complete your profile to proceed.</p>
            </div>
          )}
          {!formData.isFetching && (allowedPrivacy || allowedChantSettingsEdit) && (
            <>
              {allowedPrivacy && (
                <div className="form__group" style={{ marginBottom: '15px' }}>
                  <BasicLabel
                    name="privacyOptions"
                    required={false}
                    text="Privacy Options"
                    styles={{
                      marginBottom: '5px',
                      fontWeight: '700',
                    }}
                  />

                  {formData.options.privacy
                    .filter(privacy => ['7', '8'].indexOf(privacy.value) === -1)
                    .map(privacy => (
                      <Fragment key={privacy.value}>
                        {['6', '1'].indexOf(privacy.value) === -1 ? (
                          <div className="form__row">
                            <BasicSlideToggle
                              name="privacy"
                              labelPosition={'right'}
                              label={privacy.label}
                              value={privacy.value}
                              checked={
                                !!(
                                  values.privacy &&
                                  values.privacy.includes(privacy.value)
                                )
                              }
                              onChange={(field, value, checked) =>
                                handleChangeField(
                                  field, checked, {
                                    value,
                                    values,
                                    setFieldValue,
                                    handleSubmit,
                                    resetForm, setSubmitting, setErrors
                                  }
                                )
                              }
                              labelStyles={{
                                fontWeight: '400',
                                marginLeft: '6px',
                                marginRight: '6px',
                                textTransform: 'none'
                              }}
                              trackStyle={{
                                marginLeft: '0'
                              }}
                            />
                          </div>
                        ) : ['1'].indexOf(privacy.value) === -1 ? (
                          <div className="form__row">
                            <BasicSlideToggle
                              name="privacy"
                              labelPosition={'right'}
                              value={privacy.value}
                              checked={
                                !!(
                                  values.privacy &&
                                  values.privacy.includes(privacy.value)
                                )
                              }
                              onChange={(field, value, checked) =>
                                handleChangeField(
                                  field, checked, {
                                    value,
                                    values,
                                    setFieldValue,
                                    handleSubmit,
                                    resetForm, setSubmitting, setErrors
                                  }
                                )
                              }
                              labelStyles={{
                                fontWeight: '400',
                                marginLeft: '6px',
                                marginRight: '6px',
                                textTransform: 'none'
                              }}
                              trackStyle={{
                                marginLeft: '0'
                              }}
                              renderLabel={
                                <span style={{ display: 'flex' }}>
                                      {privacy.label}
                                  <TooltipIcon
                                    content="Your profile will be removed from searching engines."
                                    type="question"
                                  />
                                    </span>
                              }
                            />
                          </div>
                        ) : (
                          <div className="form__row">
                            <BasicSlideToggle
                              name="privacy"
                              labelPosition={'right'}
                              value={privacy.value}
                              checked={
                                !!(
                                  values.privacy &&
                                  values.privacy.includes(privacy.value)
                                )
                              }
                              onChange={(field, value, checked) =>
                                handleChangeField(
                                  field, checked, {
                                    value,
                                    values,
                                    setFieldValue,
                                    handleSubmit,
                                    resetForm, setSubmitting, setErrors
                                  }
                                )
                              }
                              labelStyles={{
                                fontWeight: '400',
                                marginLeft: '6px',
                                marginRight: '6px',
                                textTransform: 'none'
                              }}
                              trackStyle={{
                                marginLeft: '0'
                              }}
                              renderLabel={
                                <span style={{ display: 'flex' }}>
                                      {privacy.label}
                                  <TooltipIcon
                                    content="Your profile will be removed from view by employers, and only you will be able to see your profile or know of its existence. Not de-activating will allow you to viable for more career opportunities, but you can always de-activate and still apply directly to opportunities you find interesting."
                                    type="question"
                                    contentStyle={{
                                      minWidth: '252px',
                                    }}
                                  />
                                    </span>
                              }
                            />
                          </div>
                        )}

                        {Number(privacy.value) === 5 &&
                          values.privacy.includes(privacy.value) && (
                            <div className="form__group">
                              {formData.options.blackList.map(firm => (
                                <div className="form__row" key={firm.value}>
                                  <BasicSlideToggle
                                    name="blackList"
                                    labelPosition={'right'}
                                    label={firm.label}
                                    value={firm.value}
                                    checked={values.blackList.includes(
                                      firm.value
                                    )}
                                    onChange={(field, value, checked) =>
                                      handleChangeField(
                                        field, checked, {
                                          value,
                                          values,
                                          setFieldValue,
                                          handleSubmit,
                                          resetForm, setSubmitting, setErrors
                                        }
                                      )
                                    }
                                    labelStyles={{
                                      fontWeight: '400',
                                      marginLeft: '6px',
                                      marginRight: '6px',
                                      textTransform: 'none'
                                    }}
                                    trackStyle={{
                                      marginLeft: '0'
                                    }}
                                  />
                                </div>
                              ))}
                            </div>
                          )}
                      </Fragment>
                    ))}
                </div>
              )}

              {allowedChantSettingsEdit && (
                <div className="form__group" style={{ marginBottom: '15px' }}>
                  <BasicLabel
                    name="chatSettings"
                    required={false}
                    text="Chat Settings"
                    styles={{
                      marginBottom: '5px',
                      fontWeight: '700',
                    }}
                  />

                  <div className="form__row">
                    <BasicSlideToggle
                      disabled={!allowedChantGoOffline}
                      name="ChatState"
                      labelPosition={'right'}
                      label={'Go offline for GoBuyside chat'}
                      value={'Offline'}
                      checked={values.ChatState === 'Offline'}
                      onChange={(field, value, checked) =>
                        handleChangeField(
                          field, checked, {
                            value: checked ? 'Offline' : 'Online',
                            values,
                            setFieldValue,
                            handleSubmit,
                            resetForm, setSubmitting, setErrors
                          }
                        )
                      }
                      labelStyles={{
                        fontWeight: '400',
                        marginLeft: '6px',
                        marginRight: '6px',
                        textTransform: 'none'
                      }}
                      trackStyle={{
                        marginLeft: '0'
                      }}
                    />
                  </div>
                </div>
              )}
            </>
          )}
          <RequestDeleteAccount setSubmitting={setSubmitting} user={user} formData={formData} setFormData={setFormData} resetForm={resetForm} disabled={lockDeleteAccount} />
        </form>
      )}
    </Formik>
  )
}

export default PrivacySection;
